import React from 'react';
import { Link } from 'react-router-dom';
import {
  CALCULATOR_ANALYTICS_IDS,
  getDestinationDomain,
  trackCalculatorOpened,
  trackDiscoveryCallStarted,
  trackEvent,
  trackLinkClick,
  trackServicesNavClick,
} from '../lib/analytics';
import BRAND from '../config/brand';
import { MEETING_BOOKING_TYPE } from '../config/booking';
import {
  GOAL_SETTING_MEETING,
  MEETING_PATH,
  PLANNING_MEETING_REFUND_WORDING,
  PRICES,
  SERVICES_PATH,
} from '../config/planningOffers';
import { SOCIAL_LINKS } from '../config/publicSeo';
import PublicSiteNav from './PublicSiteNav';
import './PublicLinksPage.css';

const contactEmail = process.env.REACT_APP_ADVISOR_NOTIFICATION_EMAIL || 'joe@bautistaplanningandanalytics.com';
const advisorPhone = process.env.REACT_APP_ADVISOR_TEXT_PHONE || '+14582279655';
const advisorPhoneDisplay = process.env.REACT_APP_ADVISOR_TEXT_PHONE_DISPLAY || '458-227-9655';

const goalSettingItem = {
  id: 'start_financial_plan',
  type: 'primary',
  destinationType: 'internal',
  linkType: 'route',
  href: `${MEETING_PATH}?content_category=general&promotional_angle=links_primary`,
  label: GOAL_SETTING_MEETING.cta,
  description:
    `${PRICES.planningMeeting} for ${GOAL_SETTING_MEETING.durationMinutes} minutes. Get clear on your goals and how the pieces fit before deciding whether a ${PRICES.comprehensive} comprehensive plan makes sense.`,
  note: PLANNING_MEETING_REFUND_WORDING,
};

const serviceItem = {
  id: 'services_and_pricing',
  type: 'service',
  destinationType: 'internal',
  linkType: 'route',
  href: SERVICES_PATH,
  label: 'Services & Pricing',
  description:
    `See the ${PRICES.comprehensive} comprehensive financial plan and optional ongoing planning support at ${PRICES.ongoingMonthly}/month.`,
};

const howPlanningWorksItem = {
  id: 'planning_process',
  type: 'resource',
  destinationType: 'internal',
  linkType: 'route',
  href: `${SERVICES_PATH}#planning-process`,
  label: 'How Financial Planning Works',
  description:
    'Learn about the planning process, sample analyses, and what coordinated decision support looks like.',
};

const calculatorItems = [
  {
    id: 'housing_decision_calculator',
    calculatorId: 'housing_decision_calculator',
    calculatorName: 'Rent vs Buy Housing Calculator',
    marketingCalculatorName: CALCULATOR_ANALYTICS_IDS.housing_decision_calculator,
    type: 'calculator',
    destinationType: 'internal',
    linkType: 'route',
    href: '/calculators/rent-vs-buy',
    label: 'Rent vs Buy Housing Calculator',
    description:
      'Compare buying, renting and investing, down payment choices, and extra mortgage payments.',
  },
  {
    id: 'income_change_paycheck_tax_estimator',
    calculatorId: 'paycheck_tax_estimator',
    calculatorName: 'Income Change Paycheck Tax Estimator',
    marketingCalculatorName: CALCULATOR_ANALYTICS_IDS.paycheck_tax_estimator,
    type: 'calculator',
    destinationType: 'internal',
    linkType: 'route',
    href: '/paycheck-tax-estimator',
    label: 'Tax Readiness Check',
    description:
      'See whether your paycheck withholding is keeping up with your household tax complexity.',
  },
  {
    id: 'retirement_gap_estimator',
    calculatorId: 'retirement_gap_estimator',
    calculatorName: 'Retirement Gap Estimator',
    marketingCalculatorName: CALCULATOR_ANALYTICS_IDS.retirement_gap_estimator,
    type: 'calculator',
    destinationType: 'internal',
    linkType: 'route',
    href: '/retirement-gap-estimator',
    label: 'Retirement Gap Estimator',
    description: 'See whether your current savings pace is aligned with your retirement goal.',
  },
  {
    id: 'social_security_estimator',
    calculatorId: 'social_security_estimator',
    calculatorName: 'Social Security Claiming Estimator',
    marketingCalculatorName: CALCULATOR_ANALYTICS_IDS.social_security_estimator,
    type: 'calculator',
    destinationType: 'internal',
    linkType: 'route',
    href: '/social-security-estimator',
    label: 'Social Security Claiming Estimator',
    description: 'Compare claiming ages and estimate lifetime benefit tradeoffs.',
  },
  {
    id: 'roth_vs_traditional_calculator',
    calculatorId: 'roth_vs_traditional_calculator',
    calculatorName: 'Roth vs. Traditional IRA Calculator',
    marketingCalculatorName: CALCULATOR_ANALYTICS_IDS.roth_vs_traditional_calculator,
    type: 'calculator',
    destinationType: 'internal',
    linkType: 'route',
    href: '/roth-vs-traditional-calculator',
    label: 'Roth vs. Traditional IRA Calculator',
    description:
      'Compare tax now vs. tax later, including a simple retirement withdrawal and Social Security view.',
  },
  {
    id: 'investment_performance_explorer',
    calculatorId: 'investment_performance_explorer',
    calculatorName: 'Investment Performance Explorer',
    marketingCalculatorName: CALCULATOR_ANALYTICS_IDS.investment_performance_explorer,
    type: 'calculator',
    destinationType: 'internal',
    linkType: 'route',
    href: '/investment-performance-explorer',
    label: 'Investment Performance Explorer',
    description:
      'Compare historical asset-class returns and trailing performance using educational market proxies.',
  },
];

const clientItems = [
  {
    id: 'text_contact',
    type: 'client',
    destinationType: 'text',
    linkType: 'external',
    href: `sms:${advisorPhone}`,
    label: 'Text Bautista Planning and Analytics',
    description: `Text ${advisorPhoneDisplay}. Standard messaging rates may apply.`,
  },
  {
    id: 'email_contact',
    type: 'client',
    destinationType: 'email',
    linkType: 'external',
    href: `mailto:${contactEmail}`,
    label: 'Email Bautista Planning and Analytics',
    description: `Ask about planning services, pricing, or whether your situation is a good fit. ${contactEmail}`,
  },
];

const bookItems = [
  {
    id: 'book_more_you_know_more_you_grow',
    href: 'https://www.amazon.com/dp/B075SG4VZ9',
    label: 'More You Know, More You Grow',
    subtitle: 'Written by Joe Bautista',
    description:
      'Practical lessons and personal insights to help you keep learning, take meaningful action, and become a better version of yourself.',
  },
  {
    id: 'book_your_daily_cup_of_joe',
    href: 'https://www.amazon.com/dp/B093YJNHHW',
    label: 'Your Daily Cup of Joe',
    subtitle: 'Written by Joe Bautista',
    description:
      'A daily quote, a short personal reflection, and two journal prompts to help you think deeply, live intentionally, and continue growing.',
  },
];

function trackLinkItem(item) {
  const clickParams = {
    calculator_id: item.calculatorId,
    calculator_name: item.marketingCalculatorName,
    destination_type: item.destinationType,
    button_id: item.id,
    button_label: item.label,
  };

  if (item.id === 'services_and_pricing') {
    trackServicesNavClick(item.href, item.label, {
      ...clickParams,
      content_category: 'pricing',
      promotional_angle: 'links_hub',
    });
  } else {
    trackLinkClick(item.href, item.label, clickParams);
  }

  if (item.destinationType === 'booking') {
    trackDiscoveryCallStarted(item.href, item.label, {
      button_id: item.id,
      button_label: item.label,
      booking_type: MEETING_BOOKING_TYPE,
    });
  }

  if (item.marketingCalculatorName) {
    trackCalculatorOpened({
      calculatorName: item.marketingCalculatorName,
      calculatorId: item.calculatorId,
    });
  }

  trackEvent('link_menu_click', {
    button_id: item.id,
    button_label: item.label,
    destination_type: item.destinationType,
    destination_url_domain: getDestinationDomain(item.href),
    calculator_id: item.calculatorId,
    calculator_name: item.marketingCalculatorName,
  });
}

function PublicLink({ item }) {
  const className = `public-linktree-button public-linktree-button--${item.type}`;
  const children = (
    <span className="public-linktree-copy">
      <span className="public-linktree-label">{item.label}</span>
      <span className="public-linktree-description">{item.description}</span>
      {item.note ? <span className="public-linktree-note">{item.note}</span> : null}
    </span>
  );

  if (item.linkType === 'route') {
    return (
      <Link
        className={className}
        to={item.href}
        data-button-id={item.id}
        onClick={() => trackLinkItem(item)}
      >
        {children}
      </Link>
    );
  }

  const opensInNewTab =
    item.type === 'primary' ||
    item.type === 'resource' ||
    /^https?:\/\//i.test(item.href || '');

  return (
    <a
      className={className}
      href={item.href}
      data-button-id={item.id}
      target={opensInNewTab ? '_blank' : undefined}
      rel={opensInNewTab ? 'noopener noreferrer' : undefined}
      onClick={() => trackLinkItem(item)}
    >
      {children}
    </a>
  );
}

function trackBookAction(book, actionId, actionLabel) {
  trackLinkClick(book.href, actionLabel, {
    destination_type: 'external',
    button_id: actionId,
    button_label: actionLabel,
  });
  trackEvent('link_menu_click', {
    button_id: actionId,
    button_label: actionLabel,
    destination_type: 'external',
    destination_url_domain: getDestinationDomain(book.href),
  });
}

function BookCard({ book }) {
  const amazonId = `${book.id}_amazon`;

  return (
    <article className="public-linktree-book-card">
      <div className="public-linktree-copy">
        <span className="public-linktree-label">{book.label}</span>
        <span className="public-linktree-book-subtitle">{book.subtitle}</span>
        <span className="public-linktree-description">{book.description}</span>
      </div>
      <div className="public-linktree-book-actions">
        <a
          className="public-linktree-book-action public-linktree-book-action--primary"
          href={book.href}
          data-button-id={amazonId}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackBookAction(book, amazonId, 'View on Amazon')}
        >
          View on Amazon
        </a>
      </div>
    </article>
  );
}

export default function PublicLinksPage() {
  React.useEffect(() => {
    trackEvent('link_menu_view');
  }, []);

  return (
    <div className="public-linktree-page">
      <PublicSiteNav />
      <main className="public-linktree-shell">
        <a
          className="public-linktree-share"
          href={`mailto:${contactEmail}`}
          aria-label="Share by email"
          data-button-id="share_email"
          onClick={() => trackLinkClick(`mailto:${contactEmail}`, 'Share by email', {
            destination_type: 'email', button_id: 'share_email', button_label: 'Share by email',
          })}
        >
          Share
        </a>

        <header className="public-linktree-header">
          <a
            className="public-linktree-avatar"
            href="https://bautistaplanningandanalytics.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Bautista Planning and Analytics website"
            data-button-id="brand_website"
            onClick={() =>
              trackLinkClick('https://bautistaplanningandanalytics.com/', 'Bautista Planning and Analytics website', {
                destination_type: 'internal', button_id: 'brand_website', button_label: 'Bautista Planning and Analytics website',
              })
            }
          >
            <img src={BRAND.logoSrc} alt="" />
          </a>
          <h1>@BautistaPlanning</h1>
          <p>
            Turn a high income into an organized financial system—without giving up another weekend
            to spreadsheets.
          </p>
          <p className="public-linktree-supporting">
            For HENRY and dual-income households that want one shared view of cash flow, taxes,
            benefits, investments, and the goals they are funding next.
          </p>
          <nav className="public-linktree-socials" aria-label="Social media profiles">
            {SOCIAL_LINKS.map((social) => (
              <a
                key={social.id}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackLinkClick(social.href, social.label, {
                  button_id: `social_${social.id}`,
                  button_label: social.label,
                  destination_type: 'social',
                })}
              >
                {social.label}
              </a>
            ))}
          </nav>
        </header>

        <nav className="public-linktree-buttons" aria-label="Bautista Planning and Analytics links">
          <PublicLink item={goalSettingItem} />

          <section className="public-linktree-path" aria-labelledby="planning-path-heading">
            <p className="public-linktree-kicker">A clear place to begin</p>
            <h2 id="planning-path-heading">One path from financial questions to an actionable plan.</h2>
            <ol className="public-linktree-path-steps">
              <li>
                <span className="public-linktree-path-number">1</span>
                <span>
                  <strong>{PRICES.planningMeeting} {GOAL_SETTING_MEETING.name}</strong>
                  <small>Get clear on your goals, your questions, and the planning frameworks that apply to you.</small>
                </span>
              </li>
              <li>
                <span className="public-linktree-path-number">2</span>
                <span>
                  <strong>Complete the {PRICES.comprehensive} Comprehensive Financial Plan</strong>
                  <small>Build the coordinated roadmap with personalized analysis. {GOAL_SETTING_MEETING.creditWording}</small>
                </span>
              </li>
            </ol>
            <p className="public-linktree-path-note">
              The comprehensive plan is {PRICES.comprehensive} total. After the plan is complete, ongoing financial
              planning is available for {PRICES.ongoingMonthly} per month.
            </p>
          </section>

          <section className="public-linktree-section" aria-labelledby="calculator-section-heading">
            <h2 id="calculator-section-heading">Not Ready to Meet? Start with a Free Calculator</h2>
            <p className="public-linktree-section-intro">
              Or browse the full list on the{' '}
              <Link
                to="/calculators"
                onClick={() =>
                  trackLinkClick('/calculators', 'Calculators index', {
                    button_id: 'links_calculators_index',
                    button_label: 'Calculators index',
                    destination_type: 'internal',
                  })
                }
              >
                calculators page
              </Link>
              .
            </p>
            <div className="public-linktree-section-list">
              {calculatorItems.map((item) => (
                <PublicLink item={item} key={item.label} />
              ))}
            </div>
          </section>

          <PublicLink item={serviceItem} />
          <PublicLink item={howPlanningWorksItem} />

          <section className="public-linktree-section" aria-labelledby="contact-section-heading">
            <h2 id="contact-section-heading">Questions Before Booking?</h2>
            <div className="public-linktree-section-list">
              {clientItems.map((item) => (
                <PublicLink item={item} key={item.label} />
              ))}
            </div>
          </section>

          <section
            className="public-linktree-section public-linktree-section--inner-circle"
            aria-labelledby="inner-circle-section-heading"
          >
            <h2 id="inner-circle-section-heading">Personal Growth Shapes Every Part of Life</h2>
            <p className="public-linktree-section-intro">
              Financial planning is about more than numbers. It starts with the person making the
              decisions. Joe’s personal development books offer practical guidance for building the
              discipline, self-awareness, and clarity needed to thrive in your relationships,
              career, leadership, and finances.
            </p>
            <p className="public-linktree-section-quote">
              <strong>Joe’s belief:</strong> Lasting success begins with personal growth. Strengthen
              yourself physically, mentally, emotionally, and spiritually—and that growth will
              positively shape your relationships, career, leadership, and financial life.
            </p>
            <div className="public-linktree-section-list">
              {bookItems.map((book) => (
                <BookCard book={book} key={book.id} />
              ))}
            </div>
          </section>
        </nav>
      </main>
    </div>
  );
}
